import { catchError, map, Observable, of, switchMap, forkJoin } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { DualPanelItem } from '../../../../interfaces/DualPanelItem.interface';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { Router } from '@angular/router';
import { ParticipateEventContextService } from '../../../participate-event-context.service';

interface AvailableUser {
    value: string; // UUID
    uuid: string;
    name: string;
    type: string; // Email
    email: string;
}

interface AvailableUsersResponse {
    items: AvailableUser[];
    totalCount: number;
    pageSize: number;
    pageIndex: number;
    selectedIds: string[]; // UUIDs of users already in the event
    preSelected: boolean;
}

export function event_available_users(componentDataSourceService: ComponentDataSourceService) {
    let lastEventGuid: string | null = null;
    
    return {
        name: 'Available Users for Event',
        description: 'Lists all users that can be added to the event based on permissions',
        execute: (params?: LoadDataParams) => {
            
            const router = componentDataSourceService.injector.get(Router);
            const participateContext = componentDataSourceService.injector.get(
                ParticipateEventContextService
            );
            const eventGuid = participateContext.resolveEventGuid(router);
            
            if (!eventGuid) {
                console.error('[event_available_users] No event GUID found in URL');
                return of({ 
                    items: [], 
                    totalCount: 0,
                    selectedIds: [],
                    preSelected: false
                });
            }
            
            // If the event GUID has changed, log it
            if (eventGuid !== lastEventGuid) {
                lastEventGuid = eventGuid;
            }
            
            // Build query parameters
            const queryParams: any = {
                pageSize: 10000, // Large page size to get all users
                pageIndex: 0
            };
            
            // Add search parameter if provided
            if (params?.queryParams?.['search']) {
                queryParams.search = params.queryParams['search'];
            } else if (params?.filters) {
                // Check if search is in filters
                const searchFilter = params.filters.find(f => f.column === 'search');
                if (searchFilter?.value) {
                    queryParams.search = searchFilter.value;
                }
            }
            
            
            // Call the new endpoint
            return componentDataSourceService.http.get<AvailableUsersResponse>(
                `${environment.apiUrl}/events/${eventGuid}/available-users`,
                { params: queryParams }
            ).pipe(
                switchMap(response => {
                    
                    const selectedIdsSet = new Set(response.selectedIds || []);
                    
                    // Transform to DualPanelItem format - first add available users
                    const items: DualPanelItem[] = (response.items || []).map(user => ({
                        value: user.uuid,
                        name: user.name,
                        type: '', // Hide email/username
                        disabled: false
                    }));
                    
                    // If there are already-added users, fetch their details and add them to the list
                    if (selectedIdsSet.size > 0) {
                        const selectedUuids = Array.from(selectedIdsSet);
                        
                        // Fetch user details for already-added users in parallel
                        const userDetailRequests = selectedUuids.map(uuid => 
                            componentDataSourceService.http.get<any>(`${environment.apiUrl}/users/${uuid}`).pipe(
                                catchError(() => of(null))
                            )
                        );
                        
                        // Use forkJoin to wait for all user detail requests
                        return forkJoin(userDetailRequests).pipe(
                            map(userDetails => {
                                // Add already-added users to the list
                                // The dual panel component will automatically add "Allerede tilføjet" label for disabled items
                                userDetails.forEach((userDetail, index) => {
                                    if (userDetail) {
                                        const uuid = selectedUuids[index];
                                        const userName = `${userDetail.first_name || ''} ${userDetail.last_name || ''}`.trim() || 'Ukendt bruger';
                                        items.push({
                                            value: uuid,
                                            name: userName, // Don't add label - dual panel component handles it
                                            type: '', // Hide email/username
                                            disabled: true
                                        });
                                    }
                                });
                                
                                return {
                                    items: items,
                                    totalCount: items.length,
                                    pageSize: response.pageSize || 10000,
                                    pageIndex: response.pageIndex || 0,
                                    // Don't include selectedIds if we're showing them in items with label
                                    // The dual panel will handle them from the items array
                                    selectedIds: [],
                                    preSelected: false
                                };
                            })
                        );
                    }
                    
                    // No already-added users, return available users only
                    return of({
                        items: items,
                        totalCount: response.totalCount || items.length,
                        pageSize: response.pageSize || 10000,
                        pageIndex: response.pageIndex || 0,
                        selectedIds: response.selectedIds || [],
                        preSelected: response.preSelected || false
                    });
                }),
                catchError(error => {
                    console.error('[event_available_users] Error fetching available users:', error);
                    return of({ 
                        items: [], 
                        totalCount: 0,
                        selectedIds: [],
                        preSelected: false
                    });
                })
            );
        }
    };
}

